import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { SearchInput } from "../SearchInput";
import { useForm } from "react-hook-form";
import { theme } from "@src/theme";
import MultiSelect from "../MultiSelect";
import { ITag } from "@src/types/tags";
import { useFetchTagsInfiniteQuery } from "@src/queries/tagQueries";
import { useDebounce } from "@src/hooks/useDebounce";
import { AxiosResponse } from "axios";
import { ApiResponse } from "@src/types/httpApi";
import { useAppDispatch, useAppSelector } from "@src/redux/hooks";
import { setTracksTagFilter } from "@src/redux/slices/moodFactorySlice";
import { UIButton } from "../UIButton";
import styled from "styled-components";

const TagFilterSelection = () => {
  const {
    tracksTagFilter: { tags: initialTags, excludedTags: initialExcludedTags },
  } = useAppSelector((state) => state.moodFactory);
  const dispatch = useAppDispatch();
  const [selectedTags, setSelectedTags] = useState<ITag[]>(initialTags || []);
  const [selectedTagsForExclude, setSelectedTagsForExclude] = useState<ITag[]>(
    initialExcludedTags || []
  );
  const { register, watch } = useForm({
    defaultValues: { searchText: "" },
  });
  const [isIncludeMode, setIsIncludeMode] = useState(true);
  const [isExcludeMode, setIsExcludeMode] = useState(false);

  const toggleMode = (mode: "include" | "exclude") => {
    setIsIncludeMode(mode === "include");
    setIsExcludeMode(mode === "exclude");
  };
  const searchText = useDebounce<string>(watch("searchText"), 300);
  const {
    data: tagsResponse,
    isLoading: isTagsLoading,
    isError: isTagsError,
    error: tagsError,
    isFetching: isTagsFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useFetchTagsInfiniteQuery({
    searchText,
  });
  const memorizedTagsForInclude = useMemo(() => {
    const allTags =
      (
        (tagsResponse?.pages as AxiosResponse<ApiResponse<ITag[]>>[]) ?? []
      ).flatMap((response) => response.data.data) || [];
    return (
      allTags.filter(
        (tag) =>
          !selectedTagsForExclude.some((t) => t.documentId === tag.documentId)
      ) || []
    );
  }, [tagsResponse, selectedTagsForExclude]);
  const memorizedTagsForExclude = useMemo(() => {
    const allTags =
      (
        (tagsResponse?.pages as AxiosResponse<ApiResponse<ITag[]>>[]) ?? []
      ).flatMap((response) => response.data.data) || [];
    return (
      allTags.filter(
        (tag) => !selectedTags.some((t) => t.documentId === tag.documentId)
      ) || []
    );
  }, [tagsResponse, selectedTags]);

  const optionsEndRef = useRef<HTMLTableRowElement | null>(null);
  const excludeOptionsEndRef = useRef<HTMLTableRowElement | null>(null);
  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const target = entries[0];
      if (target.isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    },
    [hasNextPage, isFetchingNextPage, fetchNextPage]
  );
  useEffect(() => {
    const observer = new IntersectionObserver(handleObserver, {
      root: null,
      rootMargin: "0px",
      threshold: 0.1,
    });

    if (optionsEndRef.current) observer.observe(optionsEndRef.current);
    if (excludeOptionsEndRef.current)
      observer.observe(excludeOptionsEndRef.current);
    return () => {
      if (optionsEndRef.current) observer.unobserve(optionsEndRef.current);
      if (excludeOptionsEndRef.current)
        observer.unobserve(excludeOptionsEndRef.current);
    };
  }, [handleObserver]);

  useEffect(() => {
    dispatch(
      setTracksTagFilter({
        tags: selectedTags,
        excludedTags: selectedTagsForExclude,
      })
    );
  }, [selectedTags, selectedTagsForExclude, dispatch]);

  return (
    <div>
      <div>
        <div
          style={{
            paddingBottom: theme.margins.medium,
            gap: theme.margins.small,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <TopButtonsGroup>
            <UIButton
              style={{ position: "relative" }}
              onClick={() => toggleMode("include")}
              variant={isIncludeMode ? "primary" : "primaryOutline"}
            >
              Include
              <BadgeContainer isVisible={selectedTags.length > 0}>
                <CounterBadge>+{selectedTags.length}</CounterBadge>
              </BadgeContainer>
            </UIButton>
            <UIButton
              style={{ position: "relative" }}
              onClick={() => toggleMode("exclude")}
              variant={isExcludeMode ? "primary" : "primaryOutline"}
            >
              Exclude
              <BadgeContainer isVisible={selectedTagsForExclude.length > 0}>
                <CounterBadge>+{selectedTagsForExclude.length}</CounterBadge>
              </BadgeContainer>
            </UIButton>
          </TopButtonsGroup>
          <SearchInput
            placeholder="Search by tag name"
            {...register("searchText")}
          />
        </div>
        {isTagsError ? (
          <div>Error loading tags: {tagsError.message}</div>
        ) : (
          <>
            <div
              style={{
                position: "relative",
                minHeight: 48,
              }}
            >
              <AnimatedContainer isVisible={isIncludeMode}>
                <MultiSelect<ITag>
                  key="include-tags"
                  enableCreate={false}
                  toggleCreateModal={() => {}}
                  optionEntityName="tag"
                  options={memorizedTagsForInclude || []}
                  labelKey={"title"}
                  valueKey={"documentId"}
                  setSelected={setSelectedTags}
                  selected={selectedTags}
                  menuItems={[]}
                  loading={isTagsLoading || isTagsFetching}
                  loaderRef={optionsEndRef}
                />
              </AnimatedContainer>
              <AnimatedContainer isVisible={isExcludeMode}>
                <MultiSelect<ITag>
                  key="exclude-tags"
                  enableCreate={false}
                  toggleCreateModal={() => {}}
                  optionEntityName="tag"
                  options={memorizedTagsForExclude || []}
                  labelKey={"title"}
                  valueKey={"documentId"}
                  setSelected={setSelectedTagsForExclude}
                  selected={selectedTagsForExclude}
                  menuItems={[]}
                  loading={isTagsLoading || isTagsFetching}
                  loaderRef={excludeOptionsEndRef}
                />
              </AnimatedContainer>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const TopButtonsGroup = styled.div`
  display: flex;
  position: relative;
  gap: ${theme.margins.small};
`;
const AnimatedContainer = styled.div<{ isVisible: boolean }>`
  transition:
    opacity 0.3s,
    transform 0.3s;
  opacity: ${(props) => (props.isVisible ? 1 : 0)};
  transform: ${(props) =>
    props.isVisible ? "translateY(0)" : "translateY(50px)"};
  position: ${(props: { isVisible: boolean }) =>
    props.isVisible ? "relative" : "absolute"};
  width: 100%;
  top: 0;
  left: 0;
  pointer-events: ${(props: { isVisible: boolean }) =>
    props.isVisible ? "auto" : "none"};
  z-index: ${(props: { isVisible: boolean }) => (props.isVisible ? 5 : -1)};
`;

const BadgeContainer = styled.span<{ isVisible?: boolean }>`
  display: ${(props) => (props.isVisible ? "inline-flex" : "none")};
  position: absolute;
  top: -5px;
  right: -5px;
  z-index: 10;
`;

const CounterBadge = styled.span<{ active?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background-color: ${({ active, theme }) =>
    active ? theme.colors.darkerBg : theme.colors.primary};
  color: ${({ active, theme }) =>
    active ? theme.colors.primary : theme.colors.darkBg};
  font-size: 0.7rem;
  padding: 2px;
  font-weight: bold;
  position: relative;
  top: -5px;
  right: -5px;
`;

export default TagFilterSelection;
